import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";

import { queryClient, QueryKeys } from "@/api/core";
import { useApi } from "@/hooks";
import toast from "@/toast";

import { updateStatusCache } from "../utils";

import type { Status } from "@/schemas";

const messages = defineMessages({
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  reaction: "upvote" | "downvote";
  status: Status;
}

function useVoteOnStatus() {
  const intl = useIntl();
  const api = useApi();

  return useMutation({
    mutationFn: async (variables: Variables) => {
      return api.put(`/api/v2/statuses/${variables.status.id}/reaction`, {
        reaction: variables.reaction,
      });
    },
    async onMutate(variables) {
      const { status, reaction } = variables;

      let upvotesCount = status.upvotes_count;
      let downvotesCount = status.downvotes_count;

      if (status.reaction === "upvote" && reaction === "downvote") {
        upvotesCount -= 1;
      } else if (reaction === "upvote") {
        upvotesCount += 1;
      }

      if (status.reaction === "downvote" && reaction === "upvote") {
        downvotesCount -= 1;
      } else if (reaction === "downvote") {
        downvotesCount += 1;
      }

      const [prevStatus] = updateStatusCache(status.id, (prevStatus) => ({
        reaction: reaction,
        upvotes_count: upvotesCount,
        downvotes_count: downvotesCount,
      }));

      return { prevStatus };
    },
    onError(error: AxiosError, variables, context) {
      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      }
      // Return status back to its original state
      if (context) {
        updateStatusCache(variables.status.id, () => context.prevStatus!);
      }
    },
    onSuccess(_response, variables) {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.statuses.reactions(variables.status.id),
      });
    },
  });
}

export { useVoteOnStatus };
